import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useEffect, useMemo, useState } from 'react';
import ExpectedVsActualChart from './ExpectedVsActualChart.jsx';
import KpiReadingLog from './KpiReadingLog.jsx';
import resolveExecutivePulseKpiId from '../../utils/resolve-executive-pulse-kpi-id.js';
import {
    formatKpiValue,
    kpiHasRecordedValue
} from '../../utils/executive-pulse-kpi-value.js';

const DRILLDOWN_TABS = [
    { key: 'trend', label: 'Trend' },
    { key: 'data', label: 'Log & target' }
];

function formatKpiSource(item) {
    if (item.source === 'hub') return 'Hub';
    if (item.source === 'zoho') return 'Zoho';
    if (item.source === 'manual') return 'Manual';
    return item.source || 'Manual';
}

export default function DrillDownModal({
    item,
    onClose,
    loggableKpiIds,
    targetEditableKpiIds,
    onDataChanged,
    reportingPeriod,
    reportingYear
}) {
    const [activeTab, setActiveTab] = useState('trend');

    const effectiveKpiId = useMemo(
        () => resolveExecutivePulseKpiId(item),
        [item]
    );

    const loggableSet = useMemo(
        () => new Set((loggableKpiIds ?? []).map((id) => String(id))),
        [loggableKpiIds]
    );

    const targetEditableSet = useMemo(
        () => new Set((targetEditableKpiIds ?? []).map((id) => String(id))),
        [targetEditableKpiIds]
    );

    const canLog = Boolean(effectiveKpiId && loggableSet.has(effectiveKpiId));
    const canEditTarget = Boolean(
        effectiveKpiId && targetEditableSet.has(effectiveKpiId)
    );
    const showDataTab = Boolean(effectiveKpiId);

    const visibleTabs = useMemo(
        () =>
            showDataTab
                ? DRILLDOWN_TABS
                : DRILLDOWN_TABS.filter((t) => t.key === 'trend'),
        [showDataTab]
    );

    useEffect(() => {
        if (!item) return undefined;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [item, onClose]);

    useEffect(() => {
        setActiveTab('trend');
    }, [effectiveKpiId]);

    useEffect(() => {
        if (!showDataTab && activeTab === 'data') {
            setActiveTab('trend');
        }
    }, [showDataTab, activeTab]);

    if (!item) return null;

    return createPortal(
        <div
            className="exec-pulse-drilldown-overlay"
            onClick={onClose}
            role="presentation">
            <div
                className="exec-pulse-drilldown-panel"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="exec-pulse-drilldown-title">
                <div className="exec-pulse-drilldown-header">
                    <div className="exec-pulse-drilldown-header__main">
                        <h2 id="exec-pulse-drilldown-title">{item.title}</h2>
                        <div className="exec-pulse-drilldown-info">
                            <p>
                                <strong>Current:</strong> {formatKpiValue(item)}
                            </p>
                            <p>
                                <strong>Target:</strong> {item.target}
                            </p>
                            {kpiHasRecordedValue(item) ? (
                                <p>
                                    <strong>Source:</strong>{' '}
                                    {formatKpiSource(item)}
                                </p>
                            ) : null}
                            {item.lastRecordedAt ? (
                                <p>
                                    <strong>Last logged:</strong>{' '}
                                    {new Date(
                                        item.lastRecordedAt
                                    ).toLocaleString()}
                                </p>
                            ) : null}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="exec-pulse-drilldown-close"
                        onClick={onClose}
                        aria-label="Close">
                        ✕
                    </button>
                </div>

                <div
                    className="exec-pulse-drilldown-tabs"
                    role="tablist"
                    aria-label="Metric details">
                    {visibleTabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            id={`exec-pulse-drilldown-tab-${tab.key}`}
                            aria-selected={activeTab === tab.key}
                            aria-controls={`exec-pulse-drilldown-panel-${tab.key}`}
                            className={`exec-pulse-drilldown-tab${
                                activeTab === tab.key
                                    ? ' exec-pulse-drilldown-tab--active'
                                    : ''
                            }`}
                            onClick={() => setActiveTab(tab.key)}>
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="exec-pulse-drilldown-body">
                    {activeTab === 'trend' ? (
                        <div
                            id="exec-pulse-drilldown-panel-trend"
                            role="tabpanel"
                            aria-labelledby="exec-pulse-drilldown-tab-trend"
                            className="exec-pulse-drilldown-panel-content exec-pulse-drilldown-chart">
                            <ExpectedVsActualChart
                                item={item}
                                reportingPeriod={reportingPeriod}
                            />
                        </div>
                    ) : null}

                    {activeTab === 'data' && showDataTab ? (
                        <div
                            id="exec-pulse-drilldown-panel-data"
                            role="tabpanel"
                            aria-labelledby="exec-pulse-drilldown-tab-data"
                            className="exec-pulse-drilldown-panel-content exec-pulse-drilldown-data">
                            <KpiReadingLog
                                kpiId={effectiveKpiId}
                                canLog={canLog}
                                canEditTarget={canEditTarget}
                                target={item.target}
                                reportingYear={reportingYear}
                                onChanged={onDataChanged}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>,
        document.body
    );
}

DrillDownModal.propTypes = {
    item: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    loggableKpiIds: PropTypes.arrayOf(PropTypes.string),
    targetEditableKpiIds: PropTypes.arrayOf(PropTypes.string),
    onDataChanged: PropTypes.func,
    reportingPeriod: PropTypes.object,
    reportingYear: PropTypes.number.isRequired
};
